/* Stable availability partition across the entire filtered collection, before pagination.
 * Shopify renders each availability group; only the required page slices are fetched.
 * The native grid, counts and pagination remain the fallback when JavaScript/network fails.
 */
(() => {
  const availabilityKey = 'filter.v.availability';

  function planPage(total, available, pageSize, page) {
    const start = (page - 1) * pageSize;
    const end = Math.min(start + pageSize, total);
    const requests = [];
    for (const [inStock, groupStart, groupEnd] of [[true, 0, available], [false, available, total]]) {
      let offset = Math.max(start, groupStart) - groupStart;
      const stop = Math.min(end, groupEnd) - groupStart;
      while (offset < stop) {
        const sourcePage = Math.floor(offset / pageSize) + 1;
        const from = offset % pageSize;
        const count = Math.min(stop - offset, pageSize - from);
        requests.push({ inStock, page: sourcePage, from, count });
        offset += count;
      }
    }
    return requests;
  }

  class StockOrder extends HTMLElement {
    connectedCallback() {
      // A parser-created custom element may connect before its following grid exists.
      this.startTimer = setTimeout(() => this.render(), 0);
    }

    disconnectedCallback() {
      clearTimeout(this.startTimer);
      this.controller?.abort();
    }

    async render() {
      const container = this.closest('#ProductGridContainer');
      const grid = container?.querySelector('#product-grid');
      const total = Number(this.dataset.total);
      let available;
      const pageSize = Number(this.dataset.pageSize);
      const page = Number(this.dataset.page);
      if (!grid || this.dataset.filtered === 'true' || total === 0) return;
      if (![total, pageSize, page].every(Number.isInteger) || pageSize < 1 || page < 1) return;
      // When the complete collection is already rendered, no server round trip is needed.
      const localCards = [...grid.querySelectorAll(':scope > [data-stock-product]')];
      if (page === 1 && total <= pageSize && localCards.length === total &&
          new Set(localCards.map(card => card.dataset.stockProduct)).size === total &&
          localCards.every(card => ['true', 'false'].includes(card.dataset.stockAvailable))) {
        const ordered = [
          ...localCards.filter(card => card.dataset.stockAvailable === 'true'),
          ...localCards.filter(card => card.dataset.stockAvailable === 'false')
        ];
        ordered.forEach((card, index) => {
          if (index < 2) card.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
          card.style.order = String((index + 1) * 10);
          card.style.setProperty('--animation-order', String(index + 1));
          card.classList.add('scroll-trigger--cancel');
          grid.appendChild(card);
        });
        this.dataset.available = String(localCards.filter(card => card.dataset.stockAvailable === 'true').length);
        this.dataset.ready = 'true';
        return;
      }
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), 15000);
      const baseURL = new URL(window.location.href);
      baseURL.searchParams.delete('sections');
      baseURL.searchParams.delete(availabilityKey);
      baseURL.searchParams.set('section_id', 'tru-stock-products');
      baseURL.hash = '';
      container.setAttribute('data-stock-loading', '');
      grid.setAttribute('aria-busy', 'true');
      try {
        const responses = new Map();
        const loadGroup = (inStock, sourcePage, countOnly = false) => {
          const key = `${inStock}:${sourcePage}:${countOnly}`;
          if (responses.has(key)) return responses.get(key);
          const request = (async () => {
            const url = new URL(baseURL);
            if (countOnly) url.searchParams.set('section_id', 'tru-stock-count');
            url.searchParams.set(availabilityKey, inStock ? '1' : '0');
            url.searchParams.set('page', String(sourcePage));
            const response = await fetch(url, { signal: this.controller.signal });
            if (!response.ok) throw new Error(`Stock ordering: HTTP ${response.status}`);
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const meta = doc.querySelector('tru-stock-order');
            if (!meta || meta.dataset.filtered !== 'true') throw new Error('Stock ordering: filter unavailable');
            return { doc, meta };
          })();
          responses.set(key, request);
          return request;
        };
        // Facet counts can lag behind the filtered collection. Use the actual query total.
        const firstAvailable = await loadGroup(true, 1, page > 1);
        available = Number(firstAvailable.meta.dataset.total);
        if (!Number.isInteger(available) || available < 0 || available > total) throw new Error('Stock ordering: invalid count');
        this.dataset.available = String(available);
        const plan = planPage(total, available, pageSize, page);
        const slices = await Promise.all(plan.map(async (part) => {
          const { doc, meta } = await loadGroup(part.inStock, part.page);
          const expectedTotal = part.inStock ? available : total - available;
          // Don't silently display duplicates/holes if stock changed between requests,
          // or if Shopify ignored an unavailable/unsupported filter.
          if (!meta || Number(meta.dataset.total) !== expectedTotal || Number(meta.dataset.page) !== part.page) {
            throw new Error('Stock ordering: collection changed during loading');
          }
          const cards = [...doc.querySelectorAll('#product-grid > [data-stock-product]')];
          if (cards.some(card => card.dataset.stockAvailable !== String(part.inStock))) {
            throw new Error('Stock ordering: availability filter was not applied');
          }
          const slice = cards.slice(part.from, part.from + part.count);
          if (slice.length !== part.count) throw new Error('Stock ordering: incomplete page');
          return slice;
        }));
        if (!this.isConnected || this.controller.signal.aborted) return;
        const cards = slices.flat();
        if (new Set(cards.map(card => card.dataset.stockProduct)).size !== cards.length) {
          throw new Error('Stock ordering: duplicate product');
        }
        // Keep the existing promotional tile and video tile; their CSS order stays fixed.
        const existing = new Map([...grid.querySelectorAll(':scope > [data-stock-product]')]
          .map(card => [card.dataset.stockProduct, card]));
        const wanted = new Set(cards.map(card => card.dataset.stockProduct));
        existing.forEach((card, id) => { if (!wanted.has(id)) card.remove(); });
        // Reuse already-loaded images and product forms instead of recreating every card.
        cards.forEach((fetchedCard, index) => {
          const previous = existing.get(fetchedCard.dataset.stockProduct);
          const card = previous?.dataset.stockAvailable === fetchedCard.dataset.stockAvailable
            ? previous : document.importNode(fetchedCard, true);
          if (previous && card !== previous) previous.remove();
          if (index < 2) card.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
          card.style.order = String((index + 1) * 10);
          card.style.setProperty('--animation-order', String(index + 1));
          card.classList.add('scroll-trigger--cancel');
          grid.appendChild(card);
        });
        this.dataset.ready = 'true';
      } catch (error) {
        if (this.isConnected) {
          this.dataset.fallback = 'true';
          console.warn('Stock ordering: showing the original Shopify grid.', error);
        }
      } finally {
        clearTimeout(timeout);
        if (container.querySelector('tru-stock-order') === this) container.removeAttribute('data-stock-loading');
        grid.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('tru-stock-order')) customElements.define('tru-stock-order', StockOrder);
  // Pure planner is also used by the regression tests.
  if (typeof module !== 'undefined' && module.exports) module.exports = { planPage };
})();
